/* istanbul ignore file */
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { Separator } from "@/shared/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/shared/components/ui/sidebar";
import { SpecificationSidebar as CreateSpecificationSidebar } from "../../../features/dashboard/pages/specification/templates/create/sidebar/specification-sidebar";
import { SpecificationSidebar as ProductAnswersSpecificationSidebar } from "../../../features/dashboard/pages/specification/templates/product-answers/sidebar/specification-sidebar";
import SpecificationLayoutViewModel from "./specification-layout-view-model";

import Header from "@/features/dashboard/pages/specification/pages/create/header/header";
import { useDispatch } from "@/core/redux/store";

import { useSelector } from "@/core/redux/store";
import { useCallback, useMemo, useState } from "react";
import { setSidebarWidth } from "@/core/redux/slices/specification-list";
import { GripVertical } from "lucide-react";
import UploadIcon from "@/assets/icons/upload.svg";
import { setIsSpecificationCreateSidebarOpen } from "@/core/redux/slices/specification";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { useRef } from "react";
import { Button } from "@/shared/components/ui/button";
import { getAllItems } from "@/core/redux/helper/specification";
import { ISpecificationCategoryResponse } from "@/shared/services/interface/responses/ISpecificationResponse";
import DocumentAnswerPanel from "@/features/dashboard/pages/specification/templates/product-answers/document-answer-panel/document-answer-panel";

const toInitialCaps = (value: string) =>
  value
    .trim()
    .split(/\s+/)
    .map((word) =>
      word.length > 0 ? `${word.charAt(0).toLocaleUpperCase()}${word.slice(1)}` : word
    )
    .join(" ");

export function SpecificationLayout({
  children,
  disableEdit,
  useProductAnswersSidebar = false,
}: {
  children: React.ReactNode;
  disableEdit?: boolean;
  useProductAnswersSidebar?: boolean;
}) {
  // const openSidebar = useSelector((state) => state.specification.openSidebar);
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);
  const { specification } = SpecificationLayoutViewModel();
  const dispatch = useDispatch();
  const sidebarWidth = useSelector(
    (state) => state.specificationList.sidebarWidth
  );
  const isSpecificationCreateSidebarOpen = useSelector(
    (state) => state.specification.isSpecificationCreateSidebarOpen
  );
  const [isHoveringLayoutEdge, setIsHoveringLayoutEdge] = useState(false);
  const [isResizingLayout, setIsResizingLayout] = useState(false);
  const [isDocumentPanelOpen, setIsDocumentPanelOpen] = useState(false);
  const { t } = useTranslation();
  const supplierHashResponse = useSelector(
    (state) => state.productAnswer.supplierHashResponse
  );
  const documentAnswerSpecificationId =
    supplierHashResponse?.specificationInvite?.specificationId ||
    specification.specification.id ||
    "";
  const documentAnswerProductId =
    supplierHashResponse?.specificationInvite?.productId ||
    "";
  const productOrServiceName =
    supplierHashResponse?.product?.name ||
    supplierHashResponse?.service?.name ||
    "";
  const productOrServiceLogo =
    supplierHashResponse?.product?.logo ||
    supplierHashResponse?.service?.logo ||
    "";
  const displaySpecificationName = specification.specification.name
    ? toInitialCaps(specification.specification.name)
    : "";
  const displayProductOrServiceName = productOrServiceName
    ? toInitialCaps(productOrServiceName)
    : "";
  const supplierAndAIAnswersResponse = useSelector(
    (state) => state.productAnswer.supplierAndAIAnswersResponse
  );
  // Get root categories for segmented progress bar
  const filteredCategories = useSelector(
    (state) => state.specification.filteredCategories
  );

  // Get active item index and category info for progress bar right section
  const currentItemIndex = useSelector(
    (state) => state.specification.currentItemIndex
  );
  const activeSpecificationStep = useSelector(
    (state) => state.specification.activeSpecificationStep
  );

  // Build per-category progress data: each root category gets its own segment group
  const categorySegments = useMemo(() => {
    if (!filteredCategories || filteredCategories.length === 0) return [];

    const supplierSurveys = supplierAndAIAnswersResponse?.supplierSurveys || [];
    const answeredItemIds = new Set(supplierSurveys.map((s) => s.specificationItemId));

    return filteredCategories.map((category: ISpecificationCategoryResponse) => {
      const categoryItems = getAllItems([category]);
      const totalItems = categoryItems.length;
      const answeredItems = categoryItems.filter((item) => answeredItemIds.has(item.id)).length;
      return {
        id: category.id,
        name: category.name,
        totalItems,
        answeredItems,
      };
    });
  }, [filteredCategories, supplierAndAIAnswersResponse?.supplierSurveys]);

  // Derive totals from category segments for the progress text
  const totalItemsCount = useMemo(() => categorySegments.reduce((sum, s) => sum + s.totalItems, 0), [categorySegments]);
  const answeredCount = useMemo(() => categorySegments.reduce((sum, s) => sum + s.answeredItems, 0), [categorySegments]);
  const headerProgressPercent = totalItemsCount > 0 ? Math.round((answeredCount / totalItemsCount) * 100) : 0;

  // Find the active item's parent category name
  const activeCategoryName = useMemo(() => {
    const categoryId = activeSpecificationStep?.category?.id;
    if (!categoryId || !filteredCategories) return "";
    // Search recursively through categories to find the matching one
    const findCategoryName = (categories: ISpecificationCategoryResponse[]): string => {
      for (const cat of categories) {
        if (cat.id === categoryId) return cat.name;
        if (cat.children) {
          const subCats = cat.children.filter((c) => c.type === "category") as ISpecificationCategoryResponse[];
          const found = findCategoryName(subCats);
          if (found) return found;
        }
      }
      return "";
    };
    return findCategoryName(filteredCategories);
  }, [activeSpecificationStep?.category?.id, filteredCategories]);

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingLayout(true);
    // Dashboard sidebar'ı kapat
    dispatch(setIsSpecificationCreateSidebarOpen(false));
    // Hover timeout'u temizle
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const newWidth = e.clientX;
    if (newWidth >= 320) {
      dispatch(setSidebarWidth(newWidth));
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsResizingLayout(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  // const createSpecification = async () => {
  //   const company = me?.company;
  //   if (!company) return;

  //   const specificationResponse =
  //     await specificationService.createSpecification({
  //       companyId: company.id,
  //     });

  //   if (specificationResponse.success) {
  //     dispatch(setSpecification(specificationResponse.data));
  //     dispatch(
  //       setActiveEmptySpecification({
  //         categoryId: specificationResponse.data.createdGeneralCategory.id,
  //         isNew: true,
  //       })
  //     );
  //     const specificationCategoryResponse =
  //       await specificationService.getSpecificationCategoryAndItem({
  //         companyId: company.id,
  //         specificationId: specificationResponse.data.specification.id,
  //       });
  //     if (specificationCategoryResponse.success) {
  //       dispatch(setCategories(specificationCategoryResponse.data));
  //       dispatch(resetOpenCategories());
  //     }
  //   }
  // };
  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as React.CSSProperties
      }
      // Prevent page-level scrolling; sidebar remains independently scrollable.
      className="relative h-svh overflow-hidden"
    >
      {useProductAnswersSidebar ? (
        <ProductAnswersSpecificationSidebar />
      ) : (
        <CreateSpecificationSidebar disableEdit={disableEdit} />
      )}

      <SidebarInset className="specification-layout-inset bg-sidebar z-[11] h-svh overflow-hidden">
        {isSidebarOpen && (
          <div
            className="absolute group left-0 top-0 hover:bg-zinc-500 duration-300 cursor-col-resize z-10 flex h-4 w-3 items-center justify-center rounded-sm border bg-border"
            style={{
              marginTop: "50vh",
              transform: "translateY(-50%)",
              zIndex: 1000,
            }}
            onMouseDown={handleMouseDown}
          >
            <GripVertical className="h-2.5 w-2.5 group-hover:text-white duration-300" />
          </div>
        )}

        <div className="layout-container rounded-xl flex flex-row flex-1 relative overflow-hidden h-full">
          {/* Left column: header + content */}
          <div className="flex flex-col flex-1 min-w-0 relative">
          <div className="absolute left-0 top-[5px] bottom-[5px] h-[calc(100%-10px)]">
            <div
              className={`h-full border-l transition-all duration-200 ${
                isHoveringLayoutEdge || isResizingLayout
                  ? "border-zinc-300 rounded-l"
                  : "border-transparent"
              }`}
              style={{
                borderLeftWidth:
                  isHoveringLayoutEdge || isResizingLayout ? "3px" : "1px",
              }}
            />

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`absolute left-0 top-[5px] bottom-[5px] z-40 transition-all duration-200 ${
                      isHoveringLayoutEdge || isResizingLayout ? "w-1" : "w-0.5"
                    } bg-transparent hover:bg-zinc-200/50 ${
                      isResizingLayout ? "bg-zinc-300" : ""
                    }`}
                    style={{
                      cursor: isResizingLayout
                        ? "col-resize"
                        : isHoveringLayoutEdge
                        ? "col-resize"
                        : "ew-resize",
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseEnter={() => {
                      setIsHoveringLayoutEdge(true);
                      // Yarım saniye sonra dashboard sidebar'ı kapat
                      if (hoverTimeoutRef.current) {
                        clearTimeout(hoverTimeoutRef.current);
                      }
                      hoverTimeoutRef.current = setTimeout(() => {
                        dispatch(setIsSpecificationCreateSidebarOpen(false));
                        hoverTimeoutRef.current = null;
                      }, 500);
                    }}
                    onMouseLeave={() => {
                      if (!isResizingLayout) {
                        setIsHoveringLayoutEdge(false);
                      }
                      // Hover timeout'u temizle
                      if (hoverTimeoutRef.current) {
                        clearTimeout(hoverTimeoutRef.current);
                        hoverTimeoutRef.current = null;
                      }
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  sideOffset={8}
                  className="bg-[#09090B] text-white border-none rounded-[8px] py-1.5 px-3"
                >
                  <span className="text-[13px] font-500">
                    {t("expand-or-collapse")}
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <header
            className={`group/progress rounded-t-xl flex flex-col sticky top-0 shrink-0 z-[1000] bg-white transition-shadow ${
              useProductAnswersSidebar
                ? "shadow-none hover:shadow-[0px_7px_17px_-5px_rgba(0,0,0,0.07)]"
                : "border-b"
            }`}
          >
            {/* Header row */}
            <div className="flex justify-between items-center gap-2 h-[44px] px-3">
              <div className="w-max-w flex items-center">
                <Breadcrumb>
                  <BreadcrumbList className="flex-nowrap">
                    <BreadcrumbItem>
                      <BreadcrumbPage className="font-600 text-[#09090B] mt-[3px] cursor-default whitespace-nowrap">
                        {displaySpecificationName}
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                    {useProductAnswersSidebar && productOrServiceName && (
                      <>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                          <BreadcrumbPage className="font-600 text-[#09090B] mt-[3px] cursor-default flex items-center gap-1.5 whitespace-nowrap">
                            {productOrServiceLogo ? (
                              <img
                                src={productOrServiceLogo}
                                alt={displayProductOrServiceName}
                                width={12}
                                height={12}
                                className="w-3 h-3 rounded-sm object-cover flex-shrink-0"
                              />
                            ) : (
                              <span className="w-3 h-3 bg-zinc-900 rounded-sm inline-block flex-shrink-0" />
                            )}
                            {displayProductOrServiceName}
                          </BreadcrumbPage>
                        </BreadcrumbItem>
                      </>
                    )}
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
              {useProductAnswersSidebar ? (
                <Button
                  variant="ghost"
                  className="text-xs font-500 text-zinc-600 flex items-center gap-1.5 h-7 px-2 w-auto shrink-0"
                  onClick={() => setIsDocumentPanelOpen(true)}
                >
                  <img src={UploadIcon} alt="upload" width={12} height={12} className="shrink-0" />
                  <span className="whitespace-nowrap">{t("answer-with-document")}</span>
                </Button>
              ) : (
                isSidebarOpen && (
                  <div className="w-max-w">
                    <Header userCircle={true} />
                  </div>
                )
              )}
            </div>
            {/* Progress bar section */}
            {useProductAnswersSidebar && (
              <div>
                <div className="relative">
                  {/* Progress bar segments - full width, no padding */}
                  <div className="flex h-[4px] gap-[2px]">
                    {(() => {
                      const visibleSegments = categorySegments.filter((s) => s.totalItems > 0);
                      if (visibleSegments.length === 0) {
                        return <div className="flex-1 bg-[#D4D4D8]" />;
                      }
                      return visibleSegments.map((segment, idx) => {
                        const isFirst = idx === 0;
                        const isLast = idx === visibleSegments.length - 1;
                        const isFullyAnswered = segment.answeredItems === segment.totalItems;
                        const isPartiallyAnswered = segment.answeredItems > 0 && !isFullyAnswered;
                        const progressRatio = segment.totalItems > 0 ? segment.answeredItems / segment.totalItems : 0;
                        // Edges touching page = flat, inner edges = rounded
                        const borderRadius = isFirst && isLast
                          ? "0px"
                          : isFirst
                          ? "0px 9999px 9999px 0px"
                          : isLast
                          ? "9999px 0px 0px 9999px"
                          : "9999px";
                        return (
                          <div
                            key={segment.id}
                            className="overflow-hidden transition-all duration-300"
                            style={{
                              flex: segment.totalItems,
                              borderRadius,
                              background: isFullyAnswered
                                ? "linear-gradient(90deg, #70BC66, #599F50)"
                                : isPartiallyAnswered
                                ? `linear-gradient(90deg, #70BC66 0%, #599F50 ${progressRatio * 100}%, #D4D4D8 ${progressRatio * 100}%, #D4D4D8 100%)`
                                : "#D4D4D8",
                            }}
                          />
                        );
                      });
                    })()}
                  </div>

                  {/* Always-visible progress summary (bottom-right) */}
                  <div className="flex items-center justify-end px-4 h-8 transition-opacity group-hover/progress:opacity-0">
                    <span className="text-xs font-500 text-[#71717A]">
                      <span className="font-600 text-[#09090B]">{answeredCount}</span>
                      <span className="font-400"> / {totalItemsCount} · {headerProgressPercent}%</span>
                    </span>
                  </div>

                  {/* Hover details overlay (can cover the summary) */}
                  <div className="absolute left-0 right-0 top-[4px] z-10 opacity-0 pointer-events-none group-hover/progress:opacity-100 group-hover/progress:pointer-events-auto transition-opacity duration-200 bg-white border-x border-b border-[#E4E4E7] shadow-[0px_7px_17px_-5px_rgba(0,0,0,0.07)]">
                    <div className="flex items-start justify-between px-4 pt-3 pb-3">
                      {/* Left section - Progress percentage & count */}
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-[#09090B]">
                          {t("progress-label")}: {headerProgressPercent}%
                        </span>
                        <span className="text-xs text-[#71717A]">
                          <span className="font-semibold text-[#09090B]">{answeredCount}</span>
                          <span className="font-normal">/{totalItemsCount} {t("items-answered")}</span>
                        </span>
                      </div>
                      {/* Right section - Active item info */}
                      {currentItemIndex > 0 && (
                        <div className="flex flex-col gap-1 items-end">
                          <span className="text-sm font-semibold text-[#09090B]">
                            {t("item")} #{currentItemIndex}
                          </span>
                          {activeCategoryName && (
                            <span className="text-xs font-semibold text-[#71717A]">
                              {activeCategoryName}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </header>
          <div className="flex flex-1 flex-col gap-4 p-4 bg-white overflow-hidden">
            {children}
          </div>
          </div>
          {/* Right panel: document answer */}
          {useProductAnswersSidebar && (
            <DocumentAnswerPanel
              isOpen={isDocumentPanelOpen}
              onClose={() => setIsDocumentPanelOpen(false)}
              totalItems={totalItemsCount}
              answeredItems={answeredCount}
              canUseRealFlow={true}
              specificationId={documentAnswerSpecificationId}
              productId={documentAnswerProductId}
            />
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
